import { IllustratorColorType } from "@/common/enums";
import { ColourType, cssColourToRgba, parsePanelColourString, rgbaToCmyka } from '@/common/colours';
import SvgEditService from '@/services/svg-edit-service';
import PanelsService from '@/services/panels-service';
import { coloursServiceInstance } from '@/services/colours-service';
import { ISwatchMappingColor, metadataServiceInstance } from '@/services/metadata-service';

/**
 * Works out an element's fill or stroke colour from its pe-/pd- colour attributes, its Illustrator swatch metadata,
 * legacy pe-reg- attributes or its plain fill/stroke.
 *
 * Note: assigns an ID to the element if it has none (needed to look up its metadata).
 * @param el The element.
 * @param colourType Whether to read the fill or the stroke.
 * @returns The colour (null if none could be determined) and overprint setting.
 */
export function extractColourFrom(el: SVGElement, colourType: "fill" | "stroke"): IComplexColourData {
    const colourTypeCap = colourType.charAt(0).toUpperCase() + colourType.slice(1);

    const ret: IComplexColourData = {
        colour: null,
        overprint: PanelsService.GetCurrentPanelDesignerVersion() === "1" ? false : true,
    };

    let elID = el.getAttribute("id");
    if (!elID || elID.length == 0) {
        elID = SvgEditService.GetNextElementID();
        el.setAttribute("id", elID);
    }

    const swatchMetadata = metadataServiceInstance.getMetadataForElement(el);
    const colourStr = el.getAttribute(`pe-${colourType}`) ?? el.getAttribute(`pd-${colourType}`);

    if (colourStr) {
        ret.colour = parsePanelColourString(colourStr);
    } else if (swatchMetadata) {
        let colourInfo: ISwatchMappingColor = null;

        if('pathStyle' in swatchMetadata) {
            const style = swatchMetadata.pathStyle;
            if(style && style[`has${colourTypeCap}`]) {
                 colourInfo = style[colourType].colour;
                 ret.overprint = style[colourType].overprint;
            }
        } else if('character_metadata' in swatchMetadata && Array.isArray(swatchMetadata.character_metadata) && swatchMetadata.character_metadata.length > 0) {
            // TODO: This only shows the first colour and won't handle multi-coloured text.
            const style = swatchMetadata.character_metadata[0];
            if(style[colourTypeCap] === true) {
                colourInfo = style[`${colourTypeCap}Color`] ?? style[`${colourTypeCap}Colour`];
                ret.overprint = style[`${colourTypeCap}Overprint`];
            }
        }

        if(colourInfo) {
            switch (colourInfo.colourType) {
                case IllustratorColorType.Cmyk:
                    ret.colour = {
                        type: ColourType.ProcessColour,
                        c: colourInfo.colour.c,
                        m: colourInfo.colour.m,
                        y: colourInfo.colour.y,
                        k: colourInfo.colour.k,
                        alpha: 1.0
                    };
                    break;

                case IllustratorColorType.CustomColour:
                    {
                        const spotColId = coloursServiceInstance.FindSpotColourIdForName(colourInfo.colour.NAME);

                        if (spotColId) {
                            ret.colour = {
                                type: ColourType.RegisteredSpot,
                                spotID: spotColId,
                                tint: 1.0, // colourInfo.tint,
                                alpha: 1.0,
                            }
                        }
                    }
                    break;

                default:
                    console.warn(`Unknown ${colourType} kind ${colourInfo} for element ${elID}`);
                    break;
            }
        }
    }


    if (!ret.colour) {
        const attrVal = el.getAttribute(colourType)?.trim().toLowerCase();
        if (attrVal == "none" || (colourType == "stroke" && !attrVal)) {
            ret.colour = {
                type: ColourType.None,
            };
        } else {
            // Handle legacy
            const oldRegValue = el.getAttribute(`pe-reg-${colourType}`);
            const previewColour = el.getAttribute(`pe-preview-${colourType}`) || el.getAttribute(colourType);

            if (oldRegValue) {
                if (oldRegValue.toLowerCase() === "none") {
                    ret.colour = { type: ColourType.None };
                } else {
                    const oldFillNum = parseFloat(oldRegValue);

                    if (!isNaN(oldFillNum)) {
                        ret.colour = { type: ColourType.Registration, reg: oldFillNum, alpha: 1.0 };
                    }
                }
            } else if (previewColour) {
                const rgba = cssColourToRgba(previewColour);
                const cmyk = rgbaToCmyka(rgba);
                ret.colour = {
                    type: ColourType.ProcessColour,
                    c: cmyk.c,
                    m: cmyk.m,
                    y: cmyk.y,
                    k: cmyk.k,
                    alpha: cmyk.a,
                }
            }
        }
    }

    const overprintVal = el.getAttribute(`pe-${colourType}-overprint`);
    if (overprintVal) {
        ret.overprint = overprintVal.toLowerCase() === "true";
    }

    return ret;
}
